import { create } from '@bufbuild/protobuf';
import { createClient, Interceptor } from "@connectrpc/connect";
import { createGrpcWebTransport } from "@connectrpc/connect-web";
import { AnalysisAppApi } from '../gen/quanterix/simoa_one/analysis_app_api/v1/analysis_app_api_pb';
import { GetAssayDataRequestSchema } from '../gen/quanterix/simoa_one/analysis_app_api/v1/get_assay_data_request_pb';
import { SaveAssayAsRequestSchema } from '../gen/quanterix/simoa_one/analysis_app_api/v1/save_assay_as_request_pb';
import { AnalysisAppThunkApi } from 'quil';
import { GetExperimentReportDataRequestSchema } from '../gen/quanterix/simoa_one/analysis_app_api/v1/get_experiment_report_data_request_pb';
import { GetExperimentDataRequestSchema } from '../gen/quanterix/simoa_one/analysis_app_api/v1/get_experiment_data_request_pb';
import { AnalyzeExperimentRequestSchema } from '../gen/quanterix/simoa_one/analysis_app_api/v1/analyze_experiment_request_pb';
import { UpdateExperimentDataRequestSchema } from '../gen/quanterix/simoa_one/analysis_app_api/v1/update_experiment_data_request_pb';
import { GetUserRequestSchema } from '../gen/quanterix/simoa_one/analysis_app_api/v1/get_user_request_pb';
import { UpdateUserRequestSchema } from '../gen/quanterix/simoa_one/analysis_app_api/v1/update_user_request_pb';
import keycloak from '../keycloak';

const dataManagementAddress = `/data-manager`;

const authInterceptor: Interceptor = (next) => async(req) => {
  if (keycloak.token) {
    req.header.set("Authorization", `Bearer ${keycloak.token}`);
  }
  const res = await next(req);
  return res;
}

const transport = createGrpcWebTransport({baseUrl: dataManagementAddress, interceptors: [authInterceptor]});

const analysisAppApi = createClient(AnalysisAppApi, transport);

const apiTemp: AnalysisAppThunkApi = {
  async getAssayData(assayId: string) {
    const getAssayRequest = create(GetAssayDataRequestSchema, {assayId})
    const assayResponse = await analysisAppApi.getAssayData(getAssayRequest);
    if (!assayResponse.assayData) {
      return Promise.reject(`Assay response ${assayResponse} is missing information`);
    }
    return assayResponse.assayData;
  },

  async saveAsAssay(assayData) {
    const saveAssayAsRequest = create(SaveAssayAsRequestSchema, {newAssayName: assayData.assayMetadata?.assayName, newAssayType: assayData.assayMetadata?.assayType, newAssayData: {case: 'assayData', value: assayData}});
    const saveAssayAsResponse = await analysisAppApi.saveAssayAs(saveAssayAsRequest);
    if (!saveAssayAsResponse.assayData) {
      return Promise.reject(`Assay response ${saveAssayAsResponse} is missing information`);
    }
    return saveAssayAsResponse.assayData;
  },

  async getExperimentData(experimentId) {
    const getExperimentDataRequest = create(GetExperimentDataRequestSchema, {experimentId})
    const getExperimentDataResponse = await analysisAppApi.getExperimentData(getExperimentDataRequest);
    if (!getExperimentDataResponse.experimentData) {
      return Promise.reject(`Experiment response ${getExperimentDataResponse} is missing information`);
    }
    return getExperimentDataResponse.experimentData;
  },

  async saveExperiment(experimentData) {
    const updateExperimentDataRequest = create(UpdateExperimentDataRequestSchema, {experimentData});
    const updateExperimentDataResponse = await analysisAppApi.updateExperimentData(updateExperimentDataRequest);
    if (!updateExperimentDataResponse.experimentData) {
      return Promise.reject(`Experiment response ${updateExperimentDataResponse} is missing information`);
    }
    return updateExperimentDataResponse.experimentData;
  },

  async runAnalysis(experimentId) {
    const runAnalysisRequest = create(AnalyzeExperimentRequestSchema, {experimentId});
    const runAnalysisResponse = await analysisAppApi.analyzeExperiment(runAnalysisRequest);
    if (!runAnalysisResponse.analysisResults) {
      return Promise.reject(`Analysis response ${runAnalysisResponse} is missing information`);
    }
    return runAnalysisResponse;
  },

  async getExperimentReportData(experimentId) {
    const getExperimentReportDataRequest = create(GetExperimentReportDataRequestSchema, {experimentId});
    const getExperimentReportDataResponse = await analysisAppApi.getExperimentReportData(getExperimentReportDataRequest);
    return getExperimentReportDataResponse;
  },

  async getUser(user) {
    const getUserRequest = create(GetUserRequestSchema, {user});
    const getUserResponse = await analysisAppApi.getUser(getUserRequest);
    return getUserResponse;
  },

  async updateUser(user) {
    const updateUserRequest = create(UpdateUserRequestSchema, {user});
    const updateUserResponse = await analysisAppApi.updateUser(updateUserRequest);
    return updateUserResponse;
  }
};

export default apiTemp;